"use client";

import { Card, FormControlLabel, Skeleton, Switch } from "@mui/material";
import Grid from "@mui/material/Grid2";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import SWR_KEYS from "@/constants/swr-keys";
import useToast from "@/contexts/toast-context";
import useInserUserProfile from "@/hooks/requests/permissions/use-insert-user-profile";
import useRemoveUserProfile from "@/hooks/requests/permissions/use-remove-user-profile";
import useMutate from "@/hooks/use-mutate";
import { IProfile, ProfileCode } from "@/interfaces/permission.interfaces";
import IUser from "@/interfaces/user.interface";
import { ProfileMessages } from "@/messages/profile/profile.messages";
import { PaginatedResponse } from "@/types/requests.types";

type Props = {
  successMessage: string;
  errorMessage: string;
  user: IUser;
  permissions: PaginatedResponse<IProfile>;
  messages: ProfileMessages;
};

function PermissionsForm({ successMessage, errorMessage, user, permissions, messages }: Props) {
  const { userProfile } = user;
  const { insert, isLoading: isLoadingInsert } = useInserUserProfile();
  const { remove, isLoading: isLoadingRemove } = useRemoveUserProfile();
  const { mutate } = useMutate();
  const { showToast } = useToast();
  const isLoading = isLoadingInsert || isLoadingRemove;

  const onSuccess = () => {
    mutate(SWR_KEYS.getUserById);
    mutate(SWR_KEYS.getAllProfiles);
    mutate(SWR_KEYS.getUsers);
  };

  const handleToggleSwitch = (profile: IProfile, isChecked: boolean) => {
    const requestProps = { errorMessage, successMessage, onSuccess };

    if (profile.code === ProfileCode.ADMINISTRATOR && isChecked) {
      showToast({ text: messages.cannotRemoveAdminPermission, type: "error" });
      return;
    }

    const _id = user.id;
    const secondaryId = profile.id;
    const userId = _id;
    const profileId = secondaryId;

    const formData = { _id, secondaryId };

    if (!isChecked) {
      insert({ formData: { userId, profileId }, ...requestProps });
    } else {
      remove({ ...formData, ...requestProps });
    }
  };

  return (
    <Card sx={{ overflow: "visible" }}>
      <MDBox p={3}>
        <MDTypography variant="h5"> {messages.permissionsTitle}</MDTypography>
      </MDBox>

      <MDBox component="form" pb={3} px={3}>
        <Grid container spacing={3} gap={2}>
          {isLoading
            ? Array.from({ length: 6 }).map((_, index) => (
                <Grid key={index} size={{ xs: 12, sm: 6 }}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
              ))
            : permissions?.items.map((permission, index) => {
                const isChecked = !!userProfile.find(
                  (profile) => profile.profileId === permission.id
                );
                return (
                  <Grid key={`${permission.code}-${index}`} size={{ xs: 12, sm: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isChecked}
                          onClick={() => handleToggleSwitch(permission, isChecked)}
                        />
                      }
                      label={permission.label}
                    />
                  </Grid>
                );
              })}
        </Grid>
      </MDBox>
    </Card>
  );
}

export default PermissionsForm;
